import { Navigate, Outlet } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { Me } from "@/api/me";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/dialog";
import { QuickCreateProvider } from "@/entities/quick-create";
import { safeStorage } from "@/lib/utils";
import { CommandPalette, useCommandPaletteHotkey } from "./CommandPalette";
import { MeBoundary } from "./MeBoundary";
import { visibleWorkspaces } from "./nav";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { ViewAsBanner } from "./ViewAsBanner";

const COLLAPSE_KEY = "pulse.sidebar.collapsed";

export function StaffShell() {
  return <MeBoundary>{(me) => (me.is_staff ? <StaffShellInner me={me} /> : <Navigate to="/portal" replace />)}</MeBoundary>;
}

function StaffShellInner({ me }: { me: Me }) {
  const [collapsed, setCollapsed] = useState(() => safeStorage.get(COLLAPSE_KEY) === "1");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const workspaces = useMemo(() => visibleWorkspaces(me), [me]);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      safeStorage.set(COLLAPSE_KEY, c ? "0" : "1");
      return !c;
    });

  const togglePalette = useCallback(() => setPaletteOpen((o) => !o), []);
  useCommandPaletteHotkey(togglePalette);

  return (
    <QuickCreateProvider>
    <div className="flex h-dvh overflow-hidden bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>

      <Sidebar
        workspaces={workspaces}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        className="hidden md:flex"
      />

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-64 gap-0 p-0 sm:max-w-64">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Pulse workspaces</SheetDescription>
          <Sidebar
            workspaces={workspaces}
            collapsed={false}
            onNavigate={() => setMobileNavOpen(false)}
            className="w-full border-r-0"
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <ViewAsBanner me={me} />
        <TopBar me={me} onOpenCommandPalette={() => setPaletteOpen(true)} onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main id="main" tabIndex={-1} className="flex-1 overflow-y-auto outline-none [scrollbar-gutter:stable]">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} workspaces={workspaces} />
    </div>
    </QuickCreateProvider>
  );
}
